import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { getQuizzesByChapterId } from "../../State/Quiz/Action";
import {
  ArrowRightCircle,
  ArrowLeftCircle,
  CheckCircle,
  Timer,
  Sparkles,
  FileCheck,
} from "lucide-react";
import { CircularProgress } from "@mui/material";
import { motion } from "framer-motion";

const PlayQuiz = ({ chapterId: propChapterId }) => {
  const { chapterId: routeChapterId } = useParams();
  const chapterId = propChapterId || routeChapterId;
  const dispatch = useDispatch();

  const { quizListResponse } = useSelector((state) => state.quiz);

  const [currentIndex, setCurrentIndex] = useState(0);
  const [selectedOptions, setSelectedOptions] = useState([]);
  const [showAnswer, setShowAnswer] = useState(false);
  const [timer, setTimer] = useState(10);
  const [quizCompleted, setQuizCompleted] = useState(false);
  const [score, setScore] = useState(0);

  useEffect(() => {
    if (chapterId) {
      dispatch(getQuizzesByChapterId(chapterId));
    }
  }, [chapterId, dispatch]);

  useEffect(() => {
    if (!showAnswer) {
      const interval = setInterval(() => {
        setTimer((prev) => {
          if (prev <= 1) {
            setShowAnswer(true);
            clearInterval(interval);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(interval);
    }
  }, [currentIndex, showAnswer]);

  const currentQuiz = quizListResponse?.data?.quizzes?.[currentIndex];
  const total = quizListResponse?.data?.quizzes?.length || 0;

  const handleOptionClick = (option) => {
    if (showAnswer) return;

    if (currentQuiz?.multipleAnswers) {
      setSelectedOptions((prev) =>
        prev.includes(option)
          ? prev.filter((o) => o !== option)
          : [...prev, option]
      );
    } else {
      setSelectedOptions([option]);
    }
  };

  const isCorrect = (option) => {
    const key = `option${option}Correct`;
    return currentQuiz?.[key];
  };

  const allCorrect = () => {
    const correctOptions = ["A", "B", "C", "D", "E"].filter(
      (opt) => currentQuiz[`option${opt}Correct`]
    );
    return (
      selectedOptions.length === correctOptions.length &&
      correctOptions.every((opt) => selectedOptions.includes(opt))
    );
  };

  const handleSubmit = () => {
    if (!showAnswer) {
      if (allCorrect()) setScore((s) => s + 1);
      setShowAnswer(true);
      setTimer(0);
    }
  };

  const goToNext = () => {
    if (!showAnswer) return; // prevent moving forward without submission

    if (currentIndex < total - 1) {
      setCurrentIndex(currentIndex + 1);
      setSelectedOptions([]);
      setShowAnswer(false);
      setTimer(10);
    } else {
      setQuizCompleted(true);
    }
  };

  const goToPrevious = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
      setSelectedOptions([]);
      setShowAnswer(false);
      setTimer(10);
    }
  };

  if (!currentQuiz) {
    return (
      <div className="flex justify-center items-center h-screen">
        <CircularProgress />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-yellow-100 via-pink-100 to-purple-200 p-6 font-sans">
      <div className="max-w-3xl mx-auto bg-white rounded-xl shadow-2xl p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold text-indigo-700 flex items-center gap-2">
            <Sparkles size={24} /> Quiz {currentIndex + 1} / {total}
          </h2>
          <div className="flex items-center gap-2 text-red-500">
            <Timer size={20} /> {timer}s
          </div>
        </div>

        <motion.div
          key={currentQuiz.id}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <h3 className="text-lg font-semibold text-gray-800 mb-4">
            {currentQuiz.question}
          </h3>

          <div className="grid grid-cols-1 gap-4">
            {["A", "B", "C", "D", "E"].map((opt) => {
              const value = currentQuiz[`option${opt}`];
              if (!value) return null;
              const correct = isCorrect(opt);
              const isSelected = selectedOptions.includes(opt);

              const baseClasses = `w-full px-4 py-3 text-left rounded-lg shadow transition-all duration-300 text-sm font-medium flex items-center gap-2`;
              const selectedCorrect =
                showAnswer && correct
                  ? "bg-green-100 border border-green-500 text-green-700"
                  : "";
              const selectedIncorrect =
                showAnswer && isSelected && !correct
                  ? "bg-red-100 border border-red-500 text-red-700"
                  : "";
              const defaultStyle = !showAnswer
                ? "bg-gray-100 hover:bg-blue-100"
                : "";

              return (
                <button
                  key={opt}
                  onClick={() => handleOptionClick(opt)}
                  disabled={showAnswer}
                  className={`${baseClasses} ${selectedCorrect} ${selectedIncorrect} ${defaultStyle}`}
                >
                  <span className="flex items-center gap-2">
                    {currentQuiz.multipleAnswers ? (
                      <input
                        type="checkbox"
                        checked={isSelected}
                        readOnly
                        className="accent-blue-600"
                      />
                    ) : (
                      <input
                        type="radio"
                        checked={isSelected}
                        readOnly
                        className="accent-blue-600"
                      />
                    )}
                    <FileCheck size={18} /> {value}
                  </span>
                </button>
              );
            })}
          </div>

          {!showAnswer && (
            <div className="mt-4 text-center">
              <button
                onClick={handleSubmit}
                className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white text-sm rounded shadow"
              >
                Submit
              </button>
            </div>
          )}

          {showAnswer && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-6 p-4 bg-green-50 border-l-4 border-green-500 rounded-md text-sm text-green-700"
            >
              <p className="flex items-center gap-2 mb-2 font-semibold">
                <CheckCircle size={18} /> Explanation:
              </p>
              {currentQuiz.explanation}
            </motion.div>
          )}

          <div className="flex justify-between mt-6">
            <button
              onClick={goToPrevious}
              disabled={currentIndex === 0}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium bg-indigo-100 hover:bg-indigo-200 rounded shadow"
            >
              <ArrowLeftCircle size={20} /> Back
            </button>
            {!quizCompleted ? (
              <button
                onClick={goToNext}
                disabled={!showAnswer}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium bg-green-100 hover:bg-green-200 rounded shadow"
              >
                Next <ArrowRightCircle size={20} />
              </button>
            ) : (
              <div className="text-right w-full text-lg font-semibold text-purple-700">
                🎉 Quiz Completed! Your Score: {score} / {total}
              </div>
            )}
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default PlayQuiz;
